/**
 * Reverse geocode service implementation. It allows to convert the geographical coordinates into textual address
 * representation.
 *
 * The tomtom.reverseGeocode() method creates a new instance of service. Parameters can be passed on creation or
 * provided via convenient functions that can be chained. The method go() performs the actual call.
 *
 * The call is asynchronous therefore user have two options to handle response, either by passing callback function or
 * by using [promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise). The
 * SDK provides polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * @module Services
 * @class reverseGeocode
 */
define('services/reverseGeocode/reverseGeocode',['./reverseGeocodeBase', '../validators', '../converters'], function(reverseGeocodeBase, validators, converters) {
    'use strict';

    var reverseGeocodeBasePrototype = reverseGeocodeBase.prototype.constructor;

    function ReverseGeocode(options) {
        reverseGeocodeBasePrototype.call(this, options);

        this.fields.type = {
            defaultValue: this.TYPE.reverseGeocode,
            visible: false
        };

        /**
         * A setter and getter for 'returnSpeedLimit' attribute. Allows, if possible, to receive the speed limit
         * information at given location.
         *
         * @method returnSpeedLimit
         * @chainable
         * @param {Boolean|String|integer} [returnSpeedLimit] To enable return of the posted speed limit (where
         *     available). Given value will get converted to boolean.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {reverseGeocode|Boolean} Method returns the service instance allowing chaining or converted
         *     returnSpeedLimit value if argument is omitted.
         */
        this.fields.returnSpeedLimit = {
            validator: validators.bool
        };

        /**
         * A setter and getter for 'streetNumber' attribute. Allowing to extract more precise information regarding
         * the address to lookup.
         *
         * @method streetNumber
         * @chainable
         * @param {String} [streetNumber] If a street number is sent in along with the request, the response may
         *     include the side of the street (Left/Right) and also an offset position for that street number.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {reverseGeocode|String} Method returns the service instance allowing chaining or street number
         *     value if argument is omitted.
         */
        this.fields.streetNumber = {
            validator: validators.string
        };
        /**
         * A setter and getter for 'returnRoadUse' attribute. Allows, if possible, to receive the speed limit
         * information at given location.
         *
         * @method returnRoadUse
         * @chainable
         * @param {Boolean|String|integer} [returnRoadUse] To enable return of the road use array for reverse
         *     geocoder at street level.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {reverseGeocode|Boolean} Method returns the service instance allowing chaining or converted
         *     returnSpeedLimit value if argument is omitted.
         */
        this.fields.returnRoadUse = {
            validator: validators.bool
        };
        /**
         * A setter and getter for 'roadUse' attribute. Restricts the service response to one or many road use
         * from: LimitedAccess, Arterial, Terminal, Ramp, Rotary, LocalStreet.
         *
         * @method roadUse
         * @chainable
         * @param {Array|String} [roadUse] To restrict reverse geocoder to a certain type of roadUse. The road use
         *     array for reverse geocoder can be one or more of: LimitedAccess, Arterial, Terminal, Ramp, Rotary,
         *     LocalStreet. Array of strings or comma separated string is accepted.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {reverseGeocode|String} Method returns the service instance allowing chaining or converted
         *     roadUse value if argument is omitted.
         */
        this.fields.roadUse = {
            converter: converters.roadUse,
            validator: validators.roadUse
        };
        /**
         * A setter and getter for _'key'_ attribute. Key can be set globally when initializing map, with
         * tomtom.key or tomtom.{service}.key function. But also per service instance on initialization or
         * with this method.
         *
         * @method key
         * @chainable
         * @param {String} key API Key valid for requested service.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {service|String} Method returns the service instance allowing chaining or key value if
         *     argument is omitted.
         */
        /**
         * A setter and getter for 'position' attribute. The lon, lat location to be reverse geocoded. Among others
         * Leaflet and OpenLayers objects can be passed as argument. Following input types are allowed:
         * * array of two float values: first as latitude and second as longitude, for example [43.71,-72.48]
         * * array of two string values: first as latitude and second as longitude, for example ['43.71','-72.48']
         * * string, that contains two float values divided by comma, for example '43.71,-72.48'
         * * object, that contains two properties: _lat_ as latitude and _lon_ as longitude
         * * object, that contains two properties: _latitude_ and _longitude_
         * * Leaflet LatLng object or google.maps.LatLngLiteral, that contains two properties: _lat_ and _lng_
         * * google.maps.LatLng object, that contains two methods: _lat_ and _lng_
         *
         * @method position
         * @chainable
         * @param {String|Array|Object} [position] This is the specified longitude and latitude of the location
         *     that address is supposed to be found.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {service|String} Method returns the service instance allowing chaining or converted
         *     position value if argument is omitted.
         */
        /**
         * A setter and getter for 'spatialKeys' attribute. Enhance, if possible, response with a proprietary
         * geo-spatial key information.
         *
         * @method spatialKeys
         * @chainable
         * @param {Boolean|String|integer} [spatialKeys] If the "spatialKeys" flag is set, the response will also
         *     contain a proprietary geo-spatial key information for a specified location. Given value will get
         *     converted to boolean.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {service|Boolean} Method returns the service instance allowing chaining or converted
         *     returnSpeedLimit value if argument is omitted.
         */
        /**
         * A setter and getter for 'heading' attribute. Makes possible to get address information of road keeping
         * in mind direction
         *
         * @method heading
         * @chainable
         * @param {float|String} [heading] The directional heading of the vehicle in degrees, for travel along a
         *     segment of roadway. 0 is North, 90 is East and so on, values range from -360 to 360. The precision
         *     can include upto one decimal place.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {service|float} Method returns the service instance allowing chaining or converted
         *     heading value if argument is omitted.
         */
        /**
         * A setter and getter for 'radius' attribute. The maximal distance in meters from the specified position
         * for the service to consider.
         *
         * @method radius
         * @chainable
         * @param {integer|String} [radius] A positive (>0) integer with maximum distance in meters from the
         *     specified position for the service to consider.
         * @throws {TypeError} if the given argument cannot be converted to a valid value
         * @return {service|integer} Method returns the service instance allowing chaining or converted
         *     radius value if argument is omitted.
         */
        /**
         * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
         *
         * @method protocol
         * @chainable
         * @param {String} [protocol] Protocol type, one of _http_ or _https_.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {service|String} Method returns the service instance allowing chaining or _protocol_
         * value if argument is omitted.
         */
    }

    /**
     * A setter and getter for callback function. This function is called after receiving successful response from
     * service. It takes one argument which is the response - JSON described in go() function documentation.
     *
     * @method callback
     * @chainable
     * @param {Function} [callback] The callback function to be called on success.
     * @throws {TypeError} if the given argument is not a function
     * @return {service|Function} Method returns the service instance allowing chaining or function if
     *     argument is omitted.
     */
    /**
     * A setter and getter for fail callback function. This function is called after unsuccessful response from
     * service or communication error. It takes one argument which is the error description.
     *
     * If callback function is not passed and promise is used to handle the response this parameter is ignored.
     * When using promise errors can be handled i.e. whit second attribute of then() method or catch() method.
     *
     * By default the fail function is:
     * ```javascript
     *
     * function(error) {
       *   console.error(error);
       *   throw new Error(error);
       * }
     * ```
     *
     * @method fail
     * @chainable
     * @param {Function} [fail] The callback function to be called on failure. By default error is logged on
     *     console and thrown.
     * @throws {TypeError} if the given argument is not a function
     * @return {service|Function} Method returns the service instance allowing chaining or function if
     *     argument is omitted.
     */
    /**
     * The function performing actual call to the web service. This method can be executed multiple times, it is
     * not changing parameters itself.
     *
     * Callback function takes a response parameter and fail callback takes error as only parameter.
     *
     * The final response from service is a JSON object with the following structure:
     *
     * ```javascript
     *
     * {
         *   address: {
         *     buildingNumber: 19,
         *     streetNumber: 19,
         *     landmark: "",
         *     street: "N 2nd St",
         *     streetName: "N 2nd St",
         *     streetNameAndNumber: "",
         *     speedLimit: "",
         *     countryCode: "US",
         *     countrySubdivision: "CA",
         *     countrySecondarySubdivision: "Santa Clara",
         *     countryTertiarySubdivision: "",
         *     municipality: "San Jose",
         *     postalCode: "95113",
         *     municipalitySubdivision: "San Francisco Bay Area",
         *     freeformAddress: "19 N 2nd St, San Jose, CA, 95113"
         *   },
         *   position: "37.337083, -121.8898",
         *   roadUse: ["LocalStreet"]
         * }
     * ```
     *
     * @method go
     * @param {Function} [callback] The callback function to be called on success.
     * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
     *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
     * @return {Promise} If callback is omitted function returns promise
     *
     * @example
     * ```javascript
     * var myFunction = function(result) { console.log(result); };
     * // this is optional if you passed global key earlier
     * tomtom.searchKey(<Your API key>);
     * var reverseGeocode = tomtom.reverseGeocode().position('43.71,-72.48').go(myFunction);
     * ```
     */
    /**
     * The method to instantiates the service. The options can be passed on creation or later during the execution
     * using function with the name matching parameter name. Each option can be retrieved using the exactly same
     * function but without parameter. The instance of service can be reused by calling multiple times go()
     * function.
     *
     * @method reverseGeocode
     * @param {Object} [options] Options to be passed to the reverseGeocode call.
     * @param {String|Array|Object} [options.position] This is the specified longitude and latitude of the location
     *     that address is supposed to be found. The possible inputs are explained with position function.
     * @param {String} [options.key] API Key valid for requested service.
     * @param {Boolean} [options.spatialKeys=false] If the "spatialKeys" flag is set, the response will also
     *     contain a proprietary geo-spatial key information for a specified location.
     * @param {Boolean} [options.returnSpeedLimit=false] To enable return of the posted speed limit (where
     *     available).
     * @param {float} [options.heading] The directional heading of the vehicle in degrees, for travel along a
     *     segment of roadway. 0 is North, 90 is East and so on, values range from -360 to 360. The precision can
     *     include upto one decimal place.
     * @param {integer} [options.radius=10000] The maximum distance in meters from the specified position for the
     *     reverse geocode to consider.
     * @param {String} [options.streetNumber] If a street number is sent in along with the request, the response
     *     may include the side of the street (Left/Right) and also an offset position for that street number.
     * @param {Boolean} [options.returnRoadUse=false] To enable return of the road use array for reverse geocoder
     *     at street level.
     * @param {Array|String} [options.roadUse] To restrict reverse geocoder to a certain type of roadUse. The road
     *     use array for reverse geocoder can be one or more of: LimitedAccess, Arterial, Terminal, Ramp, Rotary,
     *     LocalStreet.
     * @param {Function} [options.fail=default] The callback function to be called on failure. By default error is
     *     logged on console and thrown.
     * @param {Function} [options.callback] The callback function to be called on success.
     * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
     * @throws {TypeError} if any of the given parameter cannot be converted to a valid value
     * @return {reverseGeocode} Method returns new object that allows communication with the service.
     *
     * @example
     * ```javascript
     *
     * tomtom.reverseGeocode().position('51.759,19.449').go().then(function(response) {
         *   console.log(response);
         * });
     * ```
     */
    ReverseGeocode.prototype = new reverseGeocodeBasePrototype();
    ReverseGeocode.prototype.constructor = ReverseGeocode;

    return function(options) {
        return (new ReverseGeocode(options)).construct();
    };
});

